import { Select } from '@/components/base/Select';
import { t } from '@/messages/zh-TW';

/**
 * 第二層版型：頂列的「切換身分」下拉。
 *
 * 原系統對應：`layout/navbar.jsp:91-97`
 *   `<c:if test="${requestScope.isMultiRoletudent}">切換身分: <select id="shitchMyRole" onchange="switchMyRole();">`
 *   選項為 `學員` / `非學員`，目前身分後面加 `(現在)`（`navbar.jsp:93-94`）。
 *   `switchMyRole()`（`navbar.jsp:72-77`）送出 `loginSwitch/switchMyRole` 並呼叫 `showProcessing()`。
 *
 * 只有多重身分（`isMultiRoletudent`）的使用者看得到 —— 因此本元件不自行判斷，
 * 由頁面層決定要不要渲染，元件只負責「有選項就畫、選了就回呼」。
 *
 * 【證據等級】§7 第 3 級（原始碼）。【雛形值待實機校準】
 */
export interface RoleOption {
  value: string;
  label: string;
}

export interface RoleSwitcherProps {
  uiId: string;
  /** 可切換的身分清單。空陣列時不渲染（對應原系統 isMultiRoletudent 為 false）。 */
  options: readonly RoleOption[];
  /** 目前身分的 value。 */
  value: string;
  /** 切換時回呼。原系統會直接 submit 表單並顯示載入遮罩，雛形只回呼。 */
  onChange(next: string): void;
  disabled?: boolean;
}

export function RoleSwitcher({ uiId, options, value, onChange, disabled }: RoleSwitcherProps) {
  if (options.length === 0) return null;

  // 原系統把「(現在)」直接串在 option 文字後面（navbar.jsp:93-94），這裡照做。
  const decorated = options.map((option) => ({
    value: option.value,
    label: option.value === value ? `${option.label}${t('role.current')}` : option.label,
  }));

  return (
    // 用 <label> 包住控制項做**隱含關聯**，而不是 htmlFor + id：
    // 第一層的 Select/TextInput 契約沒有 `id` prop（見 base/Select.tsx），htmlFor 會指到不存在的節點。
    // TODO(base): 第一層加上 id prop 之後可改回顯式關聯。
    // biome-ignore lint/a11y/noLabelWithoutControl: 控制項是第一層 <Select>，實際 render 出 <select>。
    <label data-ui-id={uiId} className="flex items-center gap-2 text-sm">
      <span data-ui-id={`${uiId}-label`}>{t('nav.switchRole')}</span>
      <Select
        uiId={`${uiId}-select`}
        value={value}
        onChange={onChange}
        options={decorated}
        {...(disabled === undefined ? {} : { disabled })}
      />
    </label>
  );
}
